import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Users, ShoppingCart, TrendingUp, Edit, Plus, Trash2, Package, Wallet, Save, Database, Upload, FileText, CheckCircle2, AlertTriangle, Gift, UserPlus, Award, Archive, RotateCcw, Search, Download, Key, Send, X, ClipboardList, BookOpen, Smartphone, Eye, ThumbsUp, ThumbsDown, Loader2, Crown, ShieldCheck, Tag, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { toast } from 'sonner';

const Admin = () => {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [overview, setOverview] = useState({});
  const [loading, setLoading] = useState(true);
  const [editMetrics, setEditMetrics] = useState(null);
  const [metricsForm, setMetricsForm] = useState({});
  const [invForm, setInvForm] = useState({ account_type: '1-step', account_size: 10000, login_id: '', password: '', server: '' });
  const [grantForm, setGrantForm] = useState({ user_email: '', plan_id: '1s-10k' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [editCreds, setEditCreds] = useState(null); // account_id whose credentials are being edited
  const [credForm, setCredForm] = useState({ login_id: '', password: '', server: '', platform: 'Exness MT5' });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { user_id, email, soft } popup state
  const [adminLogs, setAdminLogs] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [rulesConfig, setRulesConfig] = useState(null);
  const [rulesEditDraft, setRulesEditDraft] = useState(null);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [certForm, setCertForm] = useState({ user_email: '', title: '', file: null });
  const certFileRef = useRef(null);
  // AYRONN PAY (Manual UPI) state
  const [manualPayments, setManualPayments] = useState([]);
  const [mpStatusFilter, setMpStatusFilter] = useState('pending');
  const [mpActionId, setMpActionId] = useState(null); // {id, action: 'approve'|'reject'}
  const [mpActionNote, setMpActionNote] = useState('');
  const [mpScreenshotViewer, setMpScreenshotViewer] = useState(null); // mp object
  const [mpBusyId, setMpBusyId] = useState(null);
  // KYC state
  const [kycList, setKycList] = useState([]);
  const [kycStatusFilter, setKycStatusFilter] = useState('pending');
  const [kycViewer, setKycViewer] = useState(null); // {kyc, which: 'aadhar'|'pan', blobUrl}
  const [kycActionId, setKycActionId] = useState(null);
  const [kycActionNote, setKycActionNote] = useState('');
  const [kycBusyId, setKycBusyId] = useState(null);
  // Plans editor state
  const [adminPlans, setAdminPlans] = useState([]);
  const [planEdits, setPlanEdits] = useState({}); // {plan_id: {price: ..., ...}}
  const [planBusyId, setPlanBusyId] = useState(null);

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try {
      const [u, p, a, i, py, ov] = await Promise.all([
        api.get('/admin/users'), api.get('/admin/purchases'), api.get('/admin/accounts'),
        api.get('/admin/inventory'), api.get('/admin/payouts'), api.get('/admin/overview')
      ]);
      setUsers(u.data); setPurchases(p.data); setAccounts(a.data); setInventory(i.data); setPayouts(py.data); setOverview(ov.data);
      try { const af = await api.get('/admin/affiliates'); setAffiliates(af.data); } catch(e) {}
      try { const cr = await api.get('/admin/certificates'); setCertificates(cr.data); } catch(e) {}
      try { const lg = await api.get('/admin/logs?limit=150'); setAdminLogs(lg.data); } catch(e) {}
      try { const mp = await api.get('/admin/manual-payments'); setManualPayments(mp.data); } catch(e) {}
      try { const k = await api.get('/admin/kyc'); setKycList(k.data); } catch(e) {}
      try { const pl = await api.get('/admin/plans'); setAdminPlans(pl.data); } catch(e) {}
      try {
        const rls = await api.get('/trading-rules');
        setRulesConfig(rls.data);
        if (!rulesEditDraft) setRulesEditDraft(JSON.parse(JSON.stringify(rls.data.rules)));
      } catch(e) {}
    } catch (e) { toast.error('Failed to load data'); } finally { setLoading(false); }
  };

  // --- Rules editor helpers ---
  const updateRuleField = (path, value) => {
    setRulesEditDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
      ref[path[path.length - 1]] = value;
      return next;
    });
  };
  const saveRules = async () => {
    if (!rulesEditDraft) return;
    if (!window.confirm('Save new rules version? This bumps the version and updates the public /rules page immediately. Existing purchase records retain their original version.')) return;
    setRulesSaving(true);
    try {
      const { data } = await api.put('/admin/trading-rules', { rules: rulesEditDraft });
      toast.success(`Rules saved — Version ${data.version} is now live`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rules');
    } finally {
      setRulesSaving(false);
    }
  };
  const resetRulesDraft = () => {
    if (rulesConfig) setRulesEditDraft(JSON.parse(JSON.stringify(rulesConfig.rules)));
    toast.success('Draft reset to current live rules');
  };

  // --- Credentials management ---
  const startCredEdit = (acc) => {
    setEditCreds(acc.id);
    setCredForm({ login_id: acc.login_id || '', password: acc.credentials_password || '', server: acc.server || '', platform: acc.platform || 'Exness MT5' });
  };
  const saveCredentials = async (accountId) => {
    try {
      await api.put(`/admin/accounts/${accountId}/credentials`, credForm);
      toast.success('Credentials updated & user notified');
      setEditCreds(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const resendCredentials = async (accountId) => {
    try { await api.post(`/admin/accounts/${accountId}/resend-credentials`); toast.success('Notification sent to user'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // --- User delete / archive ---
  const performDelete = async () => {
    if (!deleteConfirm) return;
    const { user_id, soft, reason } = deleteConfirm;
    try {
      await api.delete(`/admin/users/${user_id}`, { params: { soft, reason: reason || '' } });
      toast.success(soft ? 'User archived' : 'User permanently deleted');
      setDeleteConfirm(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const restoreUser = async (userId) => {
    try { await api.post(`/admin/users/${userId}/restore`); toast.success('User restored'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // --- Export users ---
  const exportUsers = () => {
    const token = localStorage.getItem('access_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/export`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'ayronn-users.csv';
        link.click();
        toast.success('CSV downloaded');
      })
      .catch(() => toast.error('Export failed'));
  };

  const handleAddInventory = async () => {
    if (!invForm.login_id || !invForm.password || !invForm.server) { toast.error('Fill all fields'); return; }
    try {
      await api.post('/admin/inventory', invForm);
      toast.success('Account added to inventory');
      setInvForm({ ...invForm, login_id: '', password: '', server: '' });
      fetchAll();
    } catch (e) { toast.error('Failed to add'); }
  };

  const handleDeleteInventory = async (id) => {
    try { await api.delete(`/admin/inventory/${id}`); toast.success('Deleted'); fetchAll(); } catch (e) { toast.error('Failed'); }
  };

  const handleGrantAccount = async () => {
    if (!grantForm.user_email || !grantForm.plan_id) { toast.error('Fill all fields'); return; }
    try { const { data } = await api.post('/admin/grant-account', grantForm); toast.success(data.message); setGrantForm({ ...grantForm, user_email: '' }); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to grant account'); }
  };

  const startMetricsEdit = (acc) => {
    setEditMetrics(acc.id);
    setMetricsForm({ balance: acc.balance, equity: acc.equity, daily_pnl: acc.daily_pnl, highest_equity: acc.highest_equity || acc.equity, lowest_equity: acc.lowest_equity || acc.equity, profit_loss: acc.profit_loss, status: acc.status, trading_days: acc.trading_days, num_trades: acc.num_trades || 0, win_rate: acc.win_rate || 0 });
  };

  const saveMetrics = async (accId) => {
    try {
      const payload = {
        balance: numForSave(metricsForm.balance),
        equity: numForSave(metricsForm.equity),
        daily_pnl: numForSave(metricsForm.daily_pnl),
        highest_equity: numForSave(metricsForm.highest_equity),
        lowest_equity: numForSave(metricsForm.lowest_equity),
        profit_loss: numForSave(metricsForm.profit_loss),
        status: metricsForm.status,
        trading_days: numForSave(metricsForm.trading_days, true),
        num_trades: numForSave(metricsForm.num_trades, true),
        win_rate: numForSave(metricsForm.win_rate),
      };
      await api.put(`/admin/accounts/${accId}/metrics`, payload);
      toast.success('Metrics updated'); setEditMetrics(null); fetchAll();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handlePayoutAction = async (id, status) => {
    const note = window.prompt(`Add a ${status === 'rejected' ? 'rejection reason' : 'note'} (optional, visible to user):`, '');
    if (note === null) return; // cancelled
    try { await api.put(`/admin/payouts/${id}`, { status, admin_note: note || '' }); toast.success(`Payout ${status}`); fetchAll(); } catch (e) { toast.error('Failed'); }
  };

  const handleRecycleAccount = async (id) => {
    try { const { data } = await api.post(`/admin/recycle-account/${id}`); toast.success(data.message); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to recycle'); }
  };

  const handleGrantPhase2 = async (id) => {
    try { const { data } = await api.post(`/admin/grant-phase2/${id}`); toast.success(data.message); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleGrantFunded = async (id) => {
    try { const { data } = await api.post(`/admin/grant-funded/${id}`); toast.success(data.message); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filteredAccounts = statusFilter === 'all' ? accounts : accounts.filter(a => a.status === statusFilter);

  const [affForm, setAffForm] = useState({ email: '', promo_code: '', discount_pct: 10, commission_pct: 15 });

  const handleCreateAffiliate = async () => {
    if (!affForm.email || !affForm.promo_code) { toast.error('Email and promo code required'); return; }
    try {
      await api.post('/admin/affiliates', { ...affForm, discount_pct: parseFloat(affForm.discount_pct), commission_pct: parseFloat(affForm.commission_pct) });
      toast.success('Affiliate created');
      setAffForm({ email: '', promo_code: '', discount_pct: 10, commission_pct: 15 });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleUpdateAffiliate = async (id, patch) => {
    try { await api.put(`/admin/affiliates/${id}`, patch); toast.success('Updated'); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteAffiliate = async (id) => {
    if (!window.confirm('Remove this affiliate? Their commissions remain tracked but they lose dashboard access.')) return;
    try { await api.delete(`/admin/affiliates/${id}`); toast.success('Affiliate removed'); fetchAll(); } catch (e) { toast.error('Failed'); }
  };

  const handleCertUpload = async () => {
    if (!certForm.user_email || !certForm.title || !certForm.file) { toast.error('Fill all fields and select a file'); return; }
    const fd = new FormData();
    fd.append('file', certForm.file);
    fd.append('title', certForm.title);
    fd.append('user_email', certForm.user_email);
    try {
      await api.post(`/admin/certificates?title=${encodeURIComponent(certForm.title)}&user_email=${encodeURIComponent(certForm.user_email)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Certificate uploaded!');
      setCertForm({ user_email: '', title: '', file: null });
      if (certFileRef.current) certFileRef.current.value = '';
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const handleDeleteCert = async (id) => {
    try { await api.delete(`/admin/certificates/${id}`); toast.success('Deleted'); fetchAll(); } catch (e) { toast.error('Failed'); }
  };

  // --- AYRONN PAY (Manual UPI) ---
  const mpScreenshotUrl = (id) => {
    const token = localStorage.getItem('access_token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-payments/${id}/screenshot?_t=${token ? 1 : 0}`;
  };
  const openMpScreenshot = async (mp) => {
    // Fetch with auth header and convert to blob URL since img tags can't send Authorization
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-payments/${mp.id}/screenshot`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setMpScreenshotViewer({ ...mp, blobUrl: url });
    } catch (e) { toast.error('Failed to load screenshot'); }
  };
  const closeMpScreenshot = () => {
    if (mpScreenshotViewer?.blobUrl) URL.revokeObjectURL(mpScreenshotViewer.blobUrl);
    setMpScreenshotViewer(null);
  };
  const startMpAction = (id, action) => { setMpActionId({ id, action }); setMpActionNote(''); };
  const cancelMpAction = () => { setMpActionId(null); setMpActionNote(''); };
  const confirmMpAction = async () => {
    if (!mpActionId) return;
    const { id, action } = mpActionId;
    if (action === 'reject' && !mpActionNote.trim()) { toast.error('Please provide a reason for rejection'); return; }
    setMpBusyId(id);
    try {
      await api.post(`/admin/manual-payments/${id}/${action}`, { note: mpActionNote });
      toast.success(action === 'approve' ? 'Payment approved & account allocated!' : 'Payment rejected');
      setMpActionId(null); setMpActionNote('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    } finally { setMpBusyId(null); }
  };

  const filteredManualPayments = manualPayments.filter(mp => mpStatusFilter === 'all' || mp.status === mpStatusFilter);

  const handleGrantElite = async (uid) => {
    if (!window.confirm('Manually grant Elite tier to this user? This bypasses the 12-payout requirement.')) return;
    try { await api.post(`/admin/users/${uid}/grant-elite`); toast.success('Elite granted'); fetchAll(); } catch (e) { toast.error('Failed'); }
  };
  const handleRevokeElite = async (uid) => {
    if (!window.confirm('Revoke Elite tier from this user?')) return;
    try { await api.post(`/admin/users/${uid}/revoke-elite`); toast.success('Elite revoked'); fetchAll(); } catch (e) { toast.error('Failed'); }
  };

  // --- KYC handlers ---
  const openKycImage = async (kyc, which) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/kyc/${kyc.id}/image/${which}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      setKycViewer({ kyc, which, blobUrl: URL.createObjectURL(blob) });
    } catch (e) { toast.error('Failed to load image'); }
  };
  const closeKycViewer = () => { if (kycViewer?.blobUrl) URL.revokeObjectURL(kycViewer.blobUrl); setKycViewer(null); };
  const startKycAction = (id, action) => { setKycActionId({ id, action }); setKycActionNote(''); };
  const confirmKycAction = async () => {
    if (!kycActionId) return;
    const { id, action } = kycActionId;
    if (action === 'reject' && !kycActionNote.trim()) { toast.error('Reason required for rejection'); return; }
    setKycBusyId(id);
    try {
      await api.post(`/admin/kyc/${id}/${action}`, { note: kycActionNote });
      toast.success(action === 'approve' ? 'KYC approved' : 'KYC rejected');
      setKycActionId(null); setKycActionNote(''); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setKycBusyId(null); }
  };

  // --- Plans editor handlers ---
  const setPlanField = (planId, field, value) => {
    setPlanEdits(prev => ({ ...prev, [planId]: { ...(prev[planId] || {}), [field]: value } }));
  };
  const savePlan = async (planId) => {
    const edits = planEdits[planId] || {};
    const payload = {};
    Object.entries(edits).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) return;
      const n = Number(v);
      if (!isNaN(n)) payload[k] = n;
    });
    if (Object.keys(payload).length === 0) { toast.error('No changes to save'); return; }
    setPlanBusyId(planId);
    try {
      await api.put(`/admin/plans/${planId}`, payload);
      toast.success('Plan updated');
      setPlanEdits(prev => { const c = { ...prev }; delete c[planId]; return c; });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setPlanBusyId(null); }
  };
  const resetPlan = async (planId) => {
    if (!window.confirm('Reset this plan to default values?')) return;
    try {
      await api.post(`/admin/plans/${planId}/reset`);
      toast.success('Plan reset to default');
      setPlanEdits(prev => { const c = { ...prev }; delete c[planId]; return c; });
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };
  const filteredKyc = kycList.filter(k => kycStatusFilter === 'all' || k.status === kycStatusFilter);

  // Parse user-typed number while allowing intermediate states like '', '-', '-.', '1.'
  // Returns the raw string so negative values can be typed; coerce on save.
  const handleNumInput = (field, isInt = false) => (e) => {
    const v = e.target.value;
    setMetricsForm(prev => ({ ...prev, [field]: v === '' ? '' : (isInt ? v.replace(/[^-\d]/g, '') : v) }));
  };
  const numForSave = (v, isInt = false) => {
    if (v === '' || v === '-' || v === null || v === undefined) return 0;
    const n = isInt ? parseInt(v, 10) : parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const tabs = [
    { id: 'users', label: 'Users', icon: Users, count: users.length },
    { id: 'purchases', label: 'Purchases', icon: ShoppingCart, count: purchases.length },
    { id: 'accounts', label: 'Accounts', icon: TrendingUp, count: accounts.length },
    { id: 'inventory', label: 'Inventory', icon: Package, count: inventory.length },
    { id: 'payouts', label: 'Payouts', icon: Wallet, count: payouts.length },
    { id: 'ayronn-pay', label: 'AYRONN PAY', icon: Smartphone, count: manualPayments.filter(m => m.status === 'pending').length || null },
    { id: 'kyc', label: 'KYC', icon: ShieldCheck, count: kycList.filter(k => k.status === 'pending').length || null },
    { id: 'plans', label: 'Plans', icon: Tag },
    { id: 'affiliates', label: 'Affiliates', icon: Gift, count: affiliates.length },
    { id: 'grant', label: 'Grant Account', icon: UserPlus, count: null },
    { id: 'certificates', label: 'Certificates', icon: Award, count: certificates.length },
    { id: 'rules', label: 'Trading Rules', icon: BookOpen, count: rulesConfig?.version || null },
    { id: 'csv-upload', label: 'CSV Upload', icon: Upload, count: null },
    { id: 'logs', label: 'Activity Log', icon: ClipboardList, count: adminLogs.length },
  ];

  if (loading) return <div className="min-h-screen bg-[#050505]"><Navbar /><div className="pt-24 flex items-center justify-center"><div className="text-white">Loading...</div></div></div>;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <section className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-3xl font-black font-heading text-white mb-2">Admin Panel</h1>
          <p className="text-zinc-400 text-sm mb-8">Manage users, accounts, inventory, and payouts</p>

          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
            {[
              {l:'Active',v:overview.active||0,c:'text-brand-positive',bg:'bg-brand-positive/10'},
              {l:'Phase 1 Passed',v:overview.phase1_passed||0,c:'text-brand-primary',bg:'bg-brand-primary/10'},
              {l:'Passed',v:overview.passed||0,c:'text-blue-400',bg:'bg-blue-400/10'},
              {l:'Funded',v:overview.funded||0,c:'text-emerald-400',bg:'bg-emerald-400/10'},
              {l:'Breached',v:overview.breached||0,c:'text-red-400',bg:'bg-red-400/10'},
              {l:'Failed',v:overview.failed||0,c:'text-red-300',bg:'bg-red-300/10'},
              {l:'Inactive',v:overview.inactive||0,c:'text-yellow-400',bg:'bg-yellow-400/10'},
              {l:'Total Users',v:overview.total_users||0,c:'text-white',bg:'bg-white/5'},
            ].map((s,i) => (
              <div key={i} className={`p-3 ${s.bg} border border-white/5 rounded-xl cursor-pointer hover:border-white/20 transition-all`} onClick={() => s.l !== 'Total Users' && setStatusFilter(s.l.toLowerCase().replace(' ','_'))}>
                <div className={`text-2xl font-black font-mono ${s.c}`}>{s.v}</div>
                <div className="text-[10px] text-zinc-500 uppercase">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar pb-2">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${tab === t.id ? 'bg-brand-primary text-black' : 'bg-white/5 text-zinc-400 hover:text-white'}`} data-testid={`admin-tab-${t.id}`}>{t.label}</button>
            ))}
          </div>

          {/* Users */}
          {tab === 'users' && (
            <div className="space-y-4">
              {/* Filter Bar */}
              <div className="glass-heavy rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search by name or email..." className="w-full pl-10 pr-4 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/40 outline-none" data-testid="user-search" />
                </div>
                <select value={userStatusFilter} onChange={e => setUserStatusFilter(e.target.value)} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="user-status-filter">
                  <option value="all">All Users</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                  <option value="admin">Admins</option>
                  <option value="google">Google Users</option>
                </select>
                <button onClick={exportUsers} className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-black text-sm font-semibold rounded-lg hover:glow-primary transition-all" data-testid="export-users-btn"><Download className="w-4 h-4" /> Export CSV</button>
              </div>

              {/* Users Table */}
              <div className="glass-heavy rounded-xl overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/5">
                    <th className="text-left p-4 text-xs uppercase text-zinc-500">Name</th>
                    <th className="text-left p-4 text-xs uppercase text-zinc-500">Email</th>
                    <th className="text-left p-4 text-xs uppercase text-zinc-500">Role</th>
                    <th className="text-left p-4 text-xs uppercase text-zinc-500">Auth</th>
                    <th className="text-left p-4 text-xs uppercase text-zinc-500">Elite</th>
                    <th className="text-left p-4 text-xs uppercase text-zinc-500">Status</th>
                    <th className="text-left p-4 text-xs uppercase text-zinc-500">Created</th>
                    <th className="text-left p-4 text-xs uppercase text-zinc-500">Actions</th>
                  </tr></thead>
                  <tbody>{users.filter(u => {
                    const term = userSearch.trim().toLowerCase();
                    if (term && !(u.name || '').toLowerCase().includes(term) && !(u.email || '').toLowerCase().includes(term)) return false;
                    if (userStatusFilter === 'active' && u.archived) return false;
                    if (userStatusFilter === 'archived' && !u.archived) return false;
                    if (userStatusFilter === 'admin' && u.role !== 'admin') return false;
                    if (userStatusFilter === 'google' && u.auth_provider !== 'google') return false;
                    return true;
                  }).map((u, i) => (
                    <tr key={u.id} className={`border-b border-white/5 ${u.archived ? 'opacity-50' : ''}`} data-testid={`admin-user-${i}`}>
                      <td className="p-4 text-sm text-white">{u.name}</td>
                      <td className="p-4 text-sm text-zinc-400">{u.email}</td>
                      <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold ${u.role === 'admin' ? 'bg-brand-secondary/20 text-brand-secondary' : 'bg-white/5 text-zinc-400'}`}>{u.role.toUpperCase()}</span></td>
                      <td className="p-4 text-xs text-zinc-400">{u.auth_provider || 'email'}</td>
                      <td className="p-4">
                        {(() => {
                          const best = u.best_account_payouts || 0;
                          const req = u.elite_payouts_required || 12;
                          const eligible = u.elite_granted || best >= req;
                          return (
                            <div className="flex items-center gap-2 min-w-[160px]" data-testid={`user-elite-${i}`}>
                              {eligible ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-brand-gold/15 text-brand-gold border border-brand-gold/30 rounded-full"><Crown className="w-2.5 h-2.5" /> Elite{u.elite_granted ? ' (Granted)' : ''}</span>
                              ) : (
                                <div className="flex flex-col gap-1 w-full">
                                  <div className="flex items-center justify-between text-[10px]">
                                    <span className="text-zinc-500">{best}/{req}</span>
                                    <span className="text-zinc-600">{Math.round((best/req)*100)}%</span>
                                  </div>
                                  <div className="h-1 bg-black/40 rounded-full overflow-hidden w-full">
                                    <div className="h-full bg-brand-gold/60 rounded-full" style={{width: `${Math.min(100,(best/req)*100)}%`}} />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold ${u.archived ? 'bg-zinc-600/20 text-zinc-400' : 'bg-brand-positive/20 text-brand-positive'}`}>{u.archived ? 'ARCHIVED' : 'ACTIVE'}</span></td>
                      <td className="p-4 text-sm text-zinc-400">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="p-4 flex gap-1 flex-wrap">
                        {u.role === 'admin' ? (
                          <span className="text-[10px] text-zinc-600">Protected</span>
                        ) : u.archived ? (
                          <>
                            <button onClick={() => restoreUser(u.id)} className="flex items-center gap-1 px-2 py-1 bg-brand-positive/10 text-brand-positive text-[10px] font-bold rounded hover:bg-brand-positive/20" data-testid={`restore-user-${i}`}><RotateCcw className="w-3 h-3" /> Restore</button>
                            <button onClick={() => setDeleteConfirm({ user_id: u.id, email: u.email, soft: false, reason: '' })} className="flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 text-[10px] font-bold rounded hover:bg-red-500/20" data-testid={`purge-user-${i}`}><Trash2 className="w-3 h-3" /> Purge</button>
                          </>
                        ) : (
                          <>
                            {u.elite_granted ? (
                              <button onClick={() => handleRevokeElite(u.id)} className="flex items-center gap-1 px-2 py-1 bg-brand-gold/10 text-brand-gold text-[10px] font-bold rounded hover:bg-brand-gold/20" data-testid={`revoke-elite-${i}`}><Crown className="w-3 h-3" /> Revoke Elite</button>
                            ) : (
                              <button onClick={() => handleGrantElite(u.id)} className="flex items-center gap-1 px-2 py-1 bg-brand-gold/10 text-brand-gold text-[10px] font-bold rounded hover:bg-brand-gold/20" data-testid={`grant-elite-${i}`}><Crown className="w-3 h-3" /> Grant Elite</button>
                            )}
                            <button onClick={() => setDeleteConfirm({ user_id: u.id, email: u.email, soft: true, reason: '' })} className="flex items-center gap-1 px-2 py-1 bg-yellow-400/10 text-yellow-400 text-[10px] font-bold rounded hover:bg-yellow-400/20" data-testid={`archive-user-${i}`}><Archive className="w-3 h-3" /> Archive</button>
                            <button onClick={() => setDeleteConfirm({ user_id: u.id, email: u.email, soft: false, reason: '' })} className="flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 text-[10px] font-bold rounded hover:bg-red-500/20" data-testid={`delete-user-${i}`}><Trash2 className="w-3 h-3" /> Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirm && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
              <div className="bg-[#0A0F1C] border border-red-500/30 rounded-2xl max-w-md w-full p-6 shadow-[0_0_40px_rgba(239,68,68,0.3)]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{deleteConfirm.soft ? 'Archive User?' : 'Permanently Delete User?'}</h3>
                    <p className="text-sm text-zinc-400 mt-1">{deleteConfirm.email}</p>
                  </div>
                  <button onClick={() => setDeleteConfirm(null)} className="ml-auto p-1 rounded hover:bg-white/10"><X className="w-4 h-4 text-zinc-400" /></button>
                </div>
                {deleteConfirm.soft ? (
                  <p className="text-sm text-zinc-300 mb-4">User will be marked as <b>archived</b>. Their data is preserved and can be restored anytime.</p>
                ) : (
                  <p className="text-sm text-zinc-300 mb-4"><b className="text-red-400">This is irreversible.</b> All accounts, purchases, payouts, notifications, and chats tied to this user will be <b>permanently deleted</b>.</p>
                )}
                <div className="mb-4">
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1">Reason (optional, logged)</label>
                  <input value={deleteConfirm.reason} onChange={e => setDeleteConfirm({ ...deleteConfirm, reason: e.target.value })} placeholder="e.g., Duplicate account, fraud, user request..." className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="delete-reason-input" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-white/5 border border-white/10 text-white font-semibold rounded-lg hover:bg-white/10">Cancel</button>
                  <button onClick={performDelete} className={`flex-1 py-2.5 font-bold rounded-lg ${deleteConfirm.soft ? 'bg-yellow-400 text-black hover:bg-yellow-500' : 'bg-red-500 text-white hover:bg-red-600'}`} data-testid="confirm-delete-btn">
                    {deleteConfirm.soft ? 'Archive User' : 'Yes, Delete Forever'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Trading Rules Editor */}
          {tab === 'rules' && rulesEditDraft && (
            <div className="space-y-6" data-testid="admin-rules-editor">
              {/* Header */}
              <div className="glass-heavy rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-brand-primary/10 border border-brand-primary/30 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Trading Rules Editor</h3>
                    <p className="text-xs text-zinc-400">Live version: <b className="text-brand-primary">v{rulesConfig?.version || 1}</b> {rulesConfig?.updated_at && <>· updated {new Date(rulesConfig.updated_at).toLocaleString()}</>}</p>
                  </div>
                </div>
                <div className="flex gap-2 md:ml-auto">
                  <button onClick={resetRulesDraft} className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-zinc-300 text-sm rounded-lg hover:bg-white/10" data-testid="rules-reset-btn"><RotateCcw className="w-4 h-4" /> Reset</button>
                  <a href="/rules" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-zinc-300 text-sm rounded-lg hover:bg-white/10">Preview /rules →</a>
                  <button onClick={saveRules} disabled={rulesSaving} className="flex items-center gap-2 px-5 py-2 bg-brand-primary text-black text-sm font-bold rounded-lg hover:glow-primary transition-all disabled:opacity-50" data-testid="rules-save-btn">
                    <Save className="w-4 h-4" /> {rulesSaving ? 'Saving...' : 'Save & Publish'}
                  </button>
                </div>
              </div>

              <div className="bg-yellow-400/5 border border-yellow-400/30 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
                <div className="text-xs text-zinc-300"><b className="text-yellow-400">Version policy:</b> Saving creates a new version. Users who purchased at the previous version remain legally bound to that version. Public <code className="text-brand-primary">/rules</code> page refreshes instantly.</div>
              </div>

              {/* Per-challenge rule blocks */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {Object.entries(rulesEditDraft).map(([challengeKey, challengeData]) => {
                  const labels = { '1-step': '1-Step Challenge', '2-step': '2-Step Challenge', 'instant': 'Instant Funding' };
                  const colors = { '1-step': 'brand-primary', '2-step': '[#A78BFA]', 'instant': '[#7B61FF]' };
                  const color = colors[challengeKey] || 'brand-primary';
                  return (
                    <div key={challengeKey} className={`glass-heavy rounded-xl p-5 space-y-4 border border-${color}/20`} data-testid={`rules-block-${challengeKey}`}>
                      <div className="flex items-center justify-between">
                        <h4 className="text-base font-bold text-white">{labels[challengeKey] || challengeKey}</h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full bg-${color}/10 text-${color} border border-${color}/30 uppercase font-bold`}>{challengeData.phases === 0 ? 'no phases' : `${challengeData.phases} phase${challengeData.phases > 1 ? 's' : ''}`}</span>
                      </div>
                      {/* Top-level editable fields: description */}
                      <div>
                        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Description</label>
                        <input value={challengeData.description || ''} onChange={e => updateRuleField([challengeKey, 'description'], e.target.value)} className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid={`rules-${challengeKey}-description`} />
                      </div>
                      {/* Nested phase blocks */}
                      {Object.entries(challengeData).filter(([k, v]) => typeof v === 'object' && v !== null).map(([phaseKey, phaseData]) => (
                        <div key={phaseKey} className="bg-[#050505]/60 border border-white/5 rounded-lg p-3">
                          <div className={`text-[10px] uppercase tracking-wider text-${color} font-bold mb-2.5`}>{phaseKey.replace('_', ' ')}</div>
                          <div className="space-y-2">
                            {Object.entries(phaseData).map(([fieldKey, fieldValue]) => {
                              const isNumber = typeof fieldValue === 'number';
                              const isBool = typeof fieldValue === 'boolean';
                              return (
                                <div key={fieldKey} className="flex items-center gap-2">
                                  <label className="text-[11px] text-zinc-400 capitalize flex-1" title={fieldKey}>{fieldKey.replace(/_/g, ' ')}</label>
                                  {isBool ? (
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                      <input type="checkbox" checked={fieldValue} onChange={e => updateRuleField([challengeKey, phaseKey, fieldKey], e.target.checked)} className="w-4 h-4 rounded" data-testid={`rules-${challengeKey}-${phaseKey}-${fieldKey}`} />
                                      <span className="text-xs text-white">{fieldValue ? 'Yes' : 'No'}</span>
                                    </label>
                                  ) : (
                                    <input
                                      type={isNumber ? 'number' : 'text'}
                                      step={isNumber ? 'any' : undefined}
                                      value={fieldValue ?? ''}
                                      onChange={e => updateRuleField([challengeKey, phaseKey, fieldKey], isNumber ? (parseFloat(e.target.value) || 0) : e.target.value)}
                                      className="w-36 px-2.5 py-1 bg-[#0A0F1C] border border-white/10 rounded text-white text-xs font-mono text-right"
                                      data-testid={`rules-${challengeKey}-${phaseKey}-${fieldKey}`}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Activity Log */}
          {tab === 'logs' && (
            <div className="glass-heavy rounded-xl overflow-x-auto">
              <div className="p-4 border-b border-white/5 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-brand-primary" /><span className="text-sm text-white font-semibold">Admin Activity Log</span><span className="text-xs text-zinc-500 ml-2">Last {adminLogs.length} actions</span></div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/5">
                  <th className="text-left p-3 text-xs uppercase text-zinc-500">Time</th>
                  <th className="text-left p-3 text-xs uppercase text-zinc-500">Admin</th>
                  <th className="text-left p-3 text-xs uppercase text-zinc-500">Action</th>
                  <th className="text-left p-3 text-xs uppercase text-zinc-500">Target</th>
                  <th className="text-left p-3 text-xs uppercase text-zinc-500">Details</th>
                </tr></thead>
                <tbody>{adminLogs.length === 0 ? <tr><td colSpan="5" className="p-6 text-center text-zinc-500">No admin actions yet</td></tr> : adminLogs.map(log => (
                  <tr key={log.id} className="border-b border-white/5" data-testid={`log-${log.id}`}>
                    <td className="p-3 text-xs text-zinc-400 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="p-3 text-sm text-zinc-300">{log.admin_email}</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-brand-primary/10 text-brand-primary">{log.action}</span></td>
                    <td className="p-3 text-xs text-zinc-400">{log.target_type} · {log.target_id?.slice(-8) || '-'}</td>
                    <td className="p-3 text-xs text-zinc-500 max-w-[280px] truncate" title={JSON.stringify(log.details)}>{Object.keys(log.details || {}).length ? JSON.stringify(log.details).slice(0, 80) : '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {/* Purchases */}
          {tab === 'purchases' && (
            <div className="space-y-4">
              <div className="glass-heavy rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-brand-primary" />
                  <span className="text-sm text-zinc-300">All purchases (OxaPay Crypto + AYRONN PAY UPI + Manual Grants)</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-zinc-400">Total: <strong className="text-white">{purchases.length}</strong></span>
                  <span className="text-zinc-400">Revenue: <strong className="text-brand-primary font-mono">${purchases.reduce((s, p) => s + (p.amount || 0), 0).toLocaleString()}</strong></span>
                </div>
              </div>
              <div className="glass-heavy rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/5">
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">User</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Plan</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Amount</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Gateway</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Coupon</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Status</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Order / Payment ID</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Date</th>
                    </tr></thead>
                    <tbody>
                      {purchases.length === 0 ? (
                        <tr><td colSpan="8" className="p-8 text-center text-zinc-500">No purchases yet</td></tr>
                      ) : purchases.map((p, i) => {
                        const gw = (p.payment_gateway || 'legacy').toLowerCase();
                        const gwStyle = gw === 'ayronn_pay' ? 'bg-brand-primary/15 text-brand-primary' : gw === 'oxapay' ? 'bg-[#F7931A]/15 text-[#F7931A]' : 'bg-white/10 text-zinc-300';
                        const gwLabel = gw === 'ayronn_pay' ? 'AYRONN PAY' : gw === 'oxapay' ? 'Crypto' : gw.toUpperCase();
                        return (
                          <tr key={p.id} className="border-b border-white/5" data-testid={`admin-purchase-${i}`}>
                            <td className="p-3">
                              <div className="text-white text-xs font-semibold">{p.user_name || '—'}</div>
                              <div className="text-zinc-500 text-[10px]">{p.user_email || p.user_id}</div>
                            </td>
                            <td className="p-3 text-sm text-white">{p.plan_name}</td>
                            <td className="p-3">
                              <div className="text-brand-primary font-bold font-mono">${p.amount}</div>
                              {p.original_amount && p.original_amount !== p.amount && (
                                <div className="text-zinc-600 text-[10px] line-through font-mono">${p.original_amount}</div>
                              )}
                            </td>
                            <td className="p-3"><span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${gwStyle}`}>{gwLabel}</span></td>
                            <td className="p-3 text-xs">{p.coupon_applied ? <span className="px-2 py-0.5 bg-brand-primary/10 text-brand-primary rounded font-mono">{p.coupon_applied}</span> : <span className="text-zinc-700">—</span>}</td>
                            <td className="p-3"><span className="px-2 py-1 bg-brand-positive/20 text-brand-positive rounded text-xs font-bold uppercase">{p.status}</span></td>
                            <td className="p-3 text-[10px] text-zinc-400 font-mono">{p.payment_id || '—'}</td>
                            <td className="p-3 text-xs text-zinc-400">{new Date(p.created_at).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Accounts with Metrics Update */}
          {tab === 'accounts' && (
            <div className="space-y-4">
              <div className="glass-heavy rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-2"><Database className="w-5 h-5 text-brand-primary" /><span className="text-sm text-zinc-300">Click Edit to update metrics. Grant Phase 2 / Funded for eligible accounts.</span></div>
                <div className="flex items-center gap-2">
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-1.5 bg-[#050505] border border-white/10 rounded-lg text-white text-xs" data-testid="status-filter">
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="phase1_passed">Phase 1 Passed</option>
                    <option value="passed">Passed</option>
                    <option value="funded">Funded</option>
                    <option value="breached">Breached</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                  </select>
                  <span className="text-xs text-zinc-500">{filteredAccounts.length} accounts</span>
                </div>
              </div>
              <div className="glass-heavy rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-white/5"><th className="text-left p-3 text-xs uppercase text-zinc-500">Account</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Plan</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Balance</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Equity</th><th className="text-left p-3 text-xs uppercase text-zinc-500">P&L</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Daily PnL</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Status</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Days</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Actions</th></tr></thead>
                  <tbody>{filteredAccounts.map((a, i) => (
                    <tr key={a.id} className="border-b border-white/5" data-testid={`admin-account-${i}`}>
                      {editMetrics === a.id ? (
                        <>
                          <td colSpan="9" className="p-4">
                            <div className="bg-[#050505] border border-brand-primary/30 rounded-xl p-5">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-white font-bold">Edit: {a.account_number} — {a.plan_name}</h4>
                                <div className="flex gap-2">
                                  <button onClick={() => saveMetrics(a.id)} className="px-4 py-1.5 bg-brand-positive text-black text-xs font-bold rounded-lg flex items-center gap-1" data-testid="save-metrics-btn"><Save className="w-3 h-3" /> Save</button>
                                  <button onClick={() => setEditMetrics(null)} className="px-4 py-1.5 bg-zinc-700 text-white text-xs rounded-lg">Cancel</button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Balance</label><input type="text" inputMode="decimal" value={metricsForm.balance} onChange={handleNumInput('balance')} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" data-testid="metric-balance" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Equity</label><input type="text" inputMode="decimal" value={metricsForm.equity} onChange={handleNumInput('equity')} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" data-testid="metric-equity" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Daily PnL <span className="text-zinc-600">(- allowed)</span></label><input type="text" inputMode="decimal" value={metricsForm.daily_pnl} onChange={handleNumInput('daily_pnl')} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" data-testid="metric-daily-pnl" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Highest Equity</label><input type="text" inputMode="decimal" value={metricsForm.highest_equity} onChange={handleNumInput('highest_equity')} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Lowest Equity</label><input type="text" inputMode="decimal" value={metricsForm.lowest_equity} onChange={handleNumInput('lowest_equity')} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Trading Days</label><input type="text" inputMode="numeric" value={metricsForm.trading_days} onChange={handleNumInput('trading_days', true)} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Num Trades</label><input type="text" inputMode="numeric" value={metricsForm.num_trades} onChange={handleNumInput('num_trades', true)} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Win Rate %</label><input type="text" inputMode="decimal" value={metricsForm.win_rate} onChange={handleNumInput('win_rate')} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Status</label><select value={metricsForm.status} onChange={e => setMetricsForm({...metricsForm, status: e.target.value})} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm"><option value="active">Active</option><option value="passed">Passed</option><option value="breached">Breached</option><option value="inactive">Inactive</option><option value="archived">Archived</option><option value="failed">Failed</option><option value="funded">Funded</option><option value="pending">Pending</option></select></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">P&L <span className="text-zinc-600">(- allowed)</span></label><input type="text" inputMode="decimal" value={metricsForm.profit_loss} onChange={handleNumInput('profit_loss')} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-zinc-200 text-sm font-mono" data-testid="metric-profit-loss" /></div>
                              </div>
                              <p className="text-[10px] text-zinc-600 mt-3">Rule engine runs automatically on save. P&L auto-calculates from Balance - Account Size if not manually overridden.</p>
                            </div>
                          </td>
                        </>
                      ) : editCreds === a.id ? (
                        <>
                          <td colSpan="9" className="p-4">
                            <div className="bg-[#050505] border border-brand-primary/30 rounded-xl p-5" data-testid="credentials-editor">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-white font-bold flex items-center gap-2"><Key className="w-4 h-4 text-brand-primary" /> Assign Credentials: {a.account_number}</h4>
                                <div className="flex gap-2">
                                  <button onClick={() => saveCredentials(a.id)} className="px-4 py-1.5 bg-brand-primary text-black text-xs font-bold rounded-lg flex items-center gap-1" data-testid="save-credentials-btn"><Save className="w-3 h-3" /> Save & Notify User</button>
                                  <button onClick={() => setEditCreds(null)} className="px-4 py-1.5 bg-zinc-700 text-white text-xs rounded-lg">Cancel</button>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Login ID</label><input value={credForm.login_id} onChange={e => setCredForm({...credForm, login_id: e.target.value})} placeholder="e.g., 218400123" className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" data-testid="cred-login-input" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Password</label><input value={credForm.password} onChange={e => setCredForm({...credForm, password: e.target.value})} placeholder="Trading password" className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" data-testid="cred-password-input" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Server</label><input value={credForm.server} onChange={e => setCredForm({...credForm, server: e.target.value})} placeholder="Exness-MT5Real10" className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm font-mono" data-testid="cred-server-input" /></div>
                                <div><label className="block text-[10px] text-zinc-500 uppercase mb-1">Platform</label><select value={credForm.platform} onChange={e => setCredForm({...credForm, platform: e.target.value})} className="w-full px-3 py-2 bg-[#0A0F1C] border border-white/10 rounded-lg text-white text-sm"><option>Exness MT5</option><option>Exness MT4</option><option>MetaTrader 5</option><option>MetaTrader 4</option></select></div>
                              </div>
                              <p className="text-[10px] text-zinc-600 mt-3">User will be notified immediately when all three fields (Login, Password, Server) are filled.</p>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3 text-white font-mono">{a.account_number}</td>
                          <td className="p-3 text-zinc-400">{a.plan_name}</td>
                          <td className="p-3 text-white font-mono">${Number(a.balance).toLocaleString()}</td>
                          <td className="p-3 text-white font-mono">${Number(a.equity).toLocaleString()}</td>
                          <td className={`p-3 font-mono font-bold ${a.profit_loss >= 0 ? 'text-brand-positive' : 'text-brand-negative'}`}>${Number(a.profit_loss).toLocaleString()}</td>
                          <td className={`p-3 font-mono ${a.daily_pnl >= 0 ? 'text-brand-positive' : 'text-brand-negative'}`}>${Number(a.daily_pnl).toLocaleString()}</td>
                          <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-bold capitalize ${a.status === 'active' ? 'bg-brand-positive/20 text-brand-positive' : a.status === 'passed' || a.status === 'phase1_passed' ? 'bg-brand-primary/20 text-brand-primary' : a.status === 'funded' ? 'bg-emerald-500/20 text-emerald-400' : a.status === 'breached' ? 'bg-red-500/20 text-red-400' : a.status === 'inactive' ? 'bg-yellow-400/20 text-yellow-400' : a.status === 'archived' ? 'bg-zinc-600/20 text-zinc-400' : 'bg-brand-negative/20 text-brand-negative'}`}>{a.status === 'phase1_passed' ? 'P1 Passed' : a.status}</span>{!a.credentials_assigned && <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-yellow-400/20 text-yellow-400" title="Credentials not yet assigned">No creds</span>}</td>
                          <td className="p-3 text-zinc-400">{a.trading_days}</td>
                          <td className="p-3 flex gap-1 flex-wrap">
                            <button onClick={() => startMetricsEdit(a)} className="flex items-center gap-1 px-2 py-1 bg-brand-primary/10 text-brand-primary text-xs font-bold rounded hover:bg-brand-primary/20 transition-all" data-testid={`edit-metrics-${i}`}><Edit className="w-3 h-3" /> Edit</button>
                            <button onClick={() => startCredEdit(a)} className={`flex items-center gap-1 px-2 py-1 text-xs font-bold rounded transition-all ${a.credentials_assigned ? 'bg-white/5 text-zinc-300 hover:bg-white/10' : 'bg-yellow-400/10 text-yellow-400 hover:bg-yellow-400/20 animate-pulse'}`} data-testid={`edit-creds-${i}`}><Key className="w-3 h-3" /> Creds</button>
                            {a.credentials_assigned && (
                              <button onClick={() => resendCredentials(a.id)} className="flex items-center gap-1 px-2 py-1 bg-brand-primary/10 text-brand-primary text-xs font-bold rounded hover:bg-brand-primary/20" data-testid={`resend-creds-${i}`} title="Resend credentials notification"><Send className="w-3 h-3" /></button>
                            )}
                            {a.status === 'phase1_passed' && (
                              <button onClick={() => handleGrantPhase2(a.id)} className="flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-400 text-xs font-bold rounded hover:bg-blue-500/20 transition-all" data-testid={`grant-phase2-${i}`}>Phase 2</button>
                            )}
                            {a.status === 'passed' && (
                              <button onClick={() => handleGrantFunded(a.id)} className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-bold rounded hover:bg-emerald-500/20 transition-all" data-testid={`grant-funded-${i}`}>Fund</button>
                            )}
                            {(a.status === 'breached' || a.status === 'failed' || a.status === 'archived') && (
                              <button onClick={() => handleRecycleAccount(a.id)} className="flex items-center gap-1 px-2 py-1 bg-yellow-400/10 text-yellow-400 text-xs font-bold rounded hover:bg-yellow-400/20 transition-all" data-testid={`recycle-${i}`}>Recycle</button>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Inventory */}
          {tab === 'inventory' && (
            <div className="space-y-6">
              <div className="glass-heavy rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Add Account to Inventory</h3>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <select value={invForm.account_type} onChange={e => setInvForm({...invForm, account_type: e.target.value})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="inv-type">
                    <option value="1-step">1-Step</option><option value="2-step">2-Step</option><option value="instant">Instant</option>
                  </select>
                  <select value={invForm.account_size} onChange={e => setInvForm({...invForm, account_size: parseInt(e.target.value)})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="inv-size">
                    <option value={5000}>5K</option><option value={10000}>10K</option><option value={25000}>25K</option><option value={50000}>50K</option><option value={100000}>100K</option>
                  </select>
                  <input placeholder="Login ID" value={invForm.login_id} onChange={e => setInvForm({...invForm, login_id: e.target.value})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="inv-login" />
                  <input placeholder="Password" value={invForm.password} onChange={e => setInvForm({...invForm, password: e.target.value})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="inv-password" />
                  <input placeholder="Server" value={invForm.server} onChange={e => setInvForm({...invForm, server: e.target.value})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="inv-server" />
                  <button onClick={handleAddInventory} className="px-4 py-2 bg-brand-primary text-black font-semibold rounded-lg text-sm hover:glow-primary transition-all" data-testid="add-inventory-btn"><Plus className="w-4 h-4 inline mr-1" />Add</button>
                </div>
              </div>

              <div className="glass-heavy rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-white/5"><th className="text-left p-3 text-xs uppercase text-zinc-500">Type</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Size</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Login</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Server</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Status</th><th className="text-left p-3 text-xs uppercase text-zinc-500">Actions</th></tr></thead>
                  <tbody>{inventory.length === 0 ? (
                    <tr><td colSpan="6" className="p-8 text-center text-zinc-500">No inventory accounts. Add accounts above.</td></tr>
                  ) : inventory.map((inv, i) => (
                    <tr key={inv.id} className="border-b border-white/5" data-testid={`inventory-row-${i}`}>
                      <td className="p-3 text-white capitalize">{inv.account_type}</td>
                      <td className="p-3 text-zinc-400">${(inv.account_size/1000)}K</td>
                      <td className="p-3 text-white font-mono">{inv.login_id}</td>
                      <td className="p-3 text-zinc-400">{inv.server}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${inv.status === 'available' ? 'bg-brand-positive/20 text-brand-positive' : 'bg-yellow-400/20 text-yellow-400'}`}>{inv.status}</span></td>
                      <td className="p-3">{inv.status === 'available' && <button onClick={() => handleDeleteInventory(inv.id)} className="p-1 text-brand-negative hover:bg-brand-negative/10 rounded transition-all" data-testid={`delete-inv-${i}`}><Trash2 className="w-4 h-4" /></button>}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payouts */}
          {tab === 'payouts' && (
            <div className="glass-heavy rounded-xl overflow-hidden">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b border-white/5"><th className="text-left p-4 text-xs uppercase text-zinc-500">Account</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Amount</th><th className="text-left p-4 text-xs uppercase text-zinc-500">TRC20 Address</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Status</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Note</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Date</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Actions</th></tr></thead>
                <tbody>{payouts.length === 0 ? (
                  <tr><td colSpan="7" className="p-8 text-center text-zinc-500">No payout requests</td></tr>
                ) : payouts.map((p, i) => (
                  <tr key={p.id} className="border-b border-white/5" data-testid={`admin-payout-${i}`}>
                    <td className="p-4 text-white font-mono">{p.account_number}</td>
                    <td className="p-4 text-brand-primary font-bold font-mono">${p.amount}</td>
                    <td className="p-4 text-xs text-zinc-300 font-mono max-w-[200px]"><div className="truncate" title={p.crypto_address}>{p.crypto_address || '-'}</div><div className="text-zinc-500 text-[10px]">{p.network || 'TRC20'}</div></td>
                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold uppercase ${p.status === 'approved' ? 'bg-brand-positive/20 text-brand-positive' : p.status === 'pending' ? 'bg-yellow-400/20 text-yellow-400' : 'bg-brand-negative/20 text-brand-negative'}`}>{p.status}</span></td>
                    <td className="p-4 text-xs text-zinc-400 max-w-[200px] whitespace-normal">{p.admin_note || <span className="text-zinc-700">—</span>}</td>
                    <td className="p-4 text-zinc-400">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="p-4">{p.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => handlePayoutAction(p.id, 'approved')} className="px-3 py-1 bg-brand-positive text-black text-xs font-bold rounded" data-testid={`approve-payout-${i}`}>Approve</button>
                        <button onClick={() => handlePayoutAction(p.id, 'rejected')} className="px-3 py-1 bg-brand-negative text-white text-xs font-bold rounded" data-testid={`reject-payout-${i}`}>Reject</button>
                      </div>
                    )}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            </div>
          )}
          {/* AYRONN PAY (Manual UPI) */}
          {tab === 'ayronn-pay' && (
            <div className="space-y-4" data-testid="admin-ayronn-pay-panel">
              {/* Info banner */}
              <div className="glass-heavy rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-l-4 border-l-brand-primary">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-6 h-6 text-brand-primary" />
                  <div>
                    <div className="text-white font-bold">AYRONN PAY (Manual UPI)</div>
                    <div className="text-xs text-zinc-400">Verify UPI payments — Approve auto-allocates account · Reject sends rejection email</div>
                  </div>
                </div>
                <div className="flex gap-2 text-xs">
                  <span className="px-3 py-1.5 bg-yellow-400/10 text-yellow-400 rounded-lg font-bold">{manualPayments.filter(m => m.status === 'pending').length} Pending</span>
                  <span className="px-3 py-1.5 bg-brand-positive/10 text-brand-positive rounded-lg font-bold">{manualPayments.filter(m => m.status === 'approved').length} Approved</span>
                  <span className="px-3 py-1.5 bg-brand-negative/10 text-brand-negative rounded-lg font-bold">{manualPayments.filter(m => m.status === 'rejected').length} Rejected</span>
                </div>
              </div>

              {/* Status sub-tabs */}
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {[
                  { id: 'pending', label: 'Pending', color: 'yellow' },
                  { id: 'approved', label: 'Approved', color: 'green' },
                  { id: 'rejected', label: 'Rejected', color: 'red' },
                  { id: 'all', label: 'All', color: 'gray' },
                ].map(st => (
                  <button key={st.id} onClick={() => setMpStatusFilter(st.id)}
                    className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all whitespace-nowrap ${mpStatusFilter === st.id ? 'bg-brand-primary text-black' : 'bg-white/5 text-zinc-400 hover:text-white'}`}
                    data-testid={`mp-filter-${st.id}`}>
                    {st.label} ({st.id === 'all' ? manualPayments.length : manualPayments.filter(m => m.status === st.id).length})
                  </button>
                ))}
              </div>

              {/* Table */}
              <div className="glass-heavy rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/5">
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">User</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Plan</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Amount</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">UTR</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Phone</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Status</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Date</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Actions</th>
                    </tr></thead>
                    <tbody>{filteredManualPayments.length === 0 ? (
                      <tr><td colSpan="8" className="p-8 text-center text-zinc-500">No {mpStatusFilter === 'all' ? '' : mpStatusFilter} submissions</td></tr>
                    ) : filteredManualPayments.map((mp, i) => (
                      <tr key={mp.id} className="border-b border-white/5" data-testid={`mp-row-${i}`}>
                        <td className="p-3"><div className="text-white text-xs">{mp.full_name}</div><div className="text-zinc-500 text-[10px]">{mp.user_email}</div></td>
                        <td className="p-3"><div className="text-white text-xs">{mp.plan_name}</div>{mp.coupon_applied && <div className="text-brand-primary text-[10px]">🏷 {mp.coupon_applied}</div>}</td>
                        <td className="p-3"><div className="text-brand-primary font-bold font-mono">${mp.amount_usd}</div><div className="text-zinc-500 text-[10px] font-mono">₹{(mp.amount_inr || 0).toLocaleString('en-IN')}</div></td>
                        <td className="p-3 text-xs text-zinc-300 font-mono">{mp.utr}</td>
                        <td className="p-3 text-xs text-zinc-400 font-mono">{mp.phone}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                            mp.status === 'approved' ? 'bg-brand-positive/20 text-brand-positive' :
                            mp.status === 'rejected' ? 'bg-brand-negative/20 text-brand-negative' :
                            'bg-yellow-400/20 text-yellow-400'
                          }`}>{mp.status}</span>
                          {mp.admin_note && <div className="text-[10px] text-zinc-500 mt-1 max-w-[150px] whitespace-normal">{mp.admin_note}</div>}
                        </td>
                        <td className="p-3 text-zinc-400 text-xs">{new Date(mp.created_at).toLocaleString()}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            <button onClick={() => openMpScreenshot(mp)} className="px-2 py-1 bg-white/5 hover:bg-white/10 text-white text-[10px] font-bold rounded flex items-center gap-1" data-testid={`mp-view-${i}`}>
                              <Eye className="w-3 h-3" /> Proof
                            </button>
                            {mp.status === 'pending' && (
                              <>
                                <button onClick={() => startMpAction(mp.id, 'approve')} disabled={mpBusyId === mp.id} className="px-2 py-1 bg-brand-positive text-black text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50" data-testid={`mp-approve-${i}`}>
                                  <ThumbsUp className="w-3 h-3" /> Approve
                                </button>
                                <button onClick={() => startMpAction(mp.id, 'reject')} disabled={mpBusyId === mp.id} className="px-2 py-1 bg-brand-negative text-white text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50" data-testid={`mp-reject-${i}`}>
                                  <ThumbsDown className="w-3 h-3" /> Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* KYC */}
          {tab === 'kyc' && (
            <div className="space-y-4" data-testid="admin-kyc-panel">
              <div className="glass-heavy rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-l-4 border-l-brand-primary">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-brand-primary" />
                  <div>
                    <div className="text-white font-bold">KYC Verification Queue</div>
                    <div className="text-xs text-zinc-400">Review Aadhaar + PAN submissions for Indian compliance (DPDP / AML)</div>
                  </div>
                </div>
                <div className="flex gap-2 text-xs">
                  <span className="px-3 py-1.5 bg-yellow-400/10 text-yellow-400 rounded-lg font-bold">{kycList.filter(k => k.status === 'pending').length} Pending</span>
                  <span className="px-3 py-1.5 bg-brand-positive/10 text-brand-positive rounded-lg font-bold">{kycList.filter(k => k.status === 'approved').length} Approved</span>
                  <span className="px-3 py-1.5 bg-brand-negative/10 text-brand-negative rounded-lg font-bold">{kycList.filter(k => k.status === 'rejected').length} Rejected</span>
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {[
                  { id: 'pending', label: 'Pending' },
                  { id: 'approved', label: 'Approved' },
                  { id: 'rejected', label: 'Rejected' },
                  { id: 'all', label: 'All' },
                ].map(s => (
                  <button key={s.id} onClick={() => setKycStatusFilter(s.id)}
                    className={`px-4 py-2 rounded-lg text-xs font-bold uppercase whitespace-nowrap ${kycStatusFilter === s.id ? 'bg-brand-primary text-black' : 'bg-white/5 text-zinc-400 hover:text-white'}`}
                    data-testid={`kyc-filter-${s.id}`}>
                    {s.label} ({s.id === 'all' ? kycList.length : kycList.filter(k => k.status === s.id).length})
                  </button>
                ))}
              </div>
              <div className="glass-heavy rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/5">
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">User</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Full Name</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Aadhaar</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">PAN</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Documents</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Status</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Submitted</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Actions</th>
                    </tr></thead>
                    <tbody>{filteredKyc.length === 0 ? (
                      <tr><td colSpan="8" className="p-8 text-center text-zinc-500">No {kycStatusFilter === 'all' ? '' : kycStatusFilter} submissions</td></tr>
                    ) : filteredKyc.map((k, i) => (
                      <tr key={k.id} className="border-b border-white/5" data-testid={`kyc-row-${i}`}>
                        <td className="p-3 text-xs text-zinc-300">{k.user_email}</td>
                        <td className="p-3 text-xs text-white font-semibold">{k.full_name}</td>
                        <td className="p-3 text-xs text-zinc-300 font-mono">{k.aadhar_number.replace(/(\d{4})/g, '$1 ').trim()}</td>
                        <td className="p-3 text-xs text-zinc-300 font-mono">{k.pan_number}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <button onClick={() => openKycImage(k, 'aadhar')} className="px-2 py-1 bg-white/5 hover:bg-white/10 text-white text-[10px] font-bold rounded flex items-center gap-1" data-testid={`kyc-view-aadhar-${i}`}><Eye className="w-3 h-3" /> Aadhaar</button>
                            <button onClick={() => openKycImage(k, 'pan')} className="px-2 py-1 bg-white/5 hover:bg-white/10 text-white text-[10px] font-bold rounded flex items-center gap-1" data-testid={`kyc-view-pan-${i}`}><Eye className="w-3 h-3" /> PAN</button>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${k.status === 'approved' ? 'bg-brand-positive/20 text-brand-positive' : k.status === 'rejected' ? 'bg-brand-negative/20 text-brand-negative' : 'bg-yellow-400/20 text-yellow-400'}`}>{k.status}</span>
                          {k.admin_note && <div className="text-[10px] text-zinc-500 mt-1 max-w-[150px] whitespace-normal">{k.admin_note}</div>}
                        </td>
                        <td className="p-3 text-zinc-400 text-xs">{new Date(k.submitted_at).toLocaleString()}</td>
                        <td className="p-3">
                          {k.status === 'pending' && (
                            <div className="flex gap-1">
                              <button onClick={() => startKycAction(k.id, 'approve')} disabled={kycBusyId === k.id} className="px-2 py-1 bg-brand-positive text-black text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50" data-testid={`kyc-approve-${i}`}><ThumbsUp className="w-3 h-3" /> Approve</button>
                              <button onClick={() => startKycAction(k.id, 'reject')} disabled={kycBusyId === k.id} className="px-2 py-1 bg-brand-negative text-white text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50" data-testid={`kyc-reject-${i}`}><ThumbsDown className="w-3 h-3" /> Reject</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Plans Editor */}
          {tab === 'plans' && (
            <div className="space-y-4" data-testid="admin-plans-panel">
              <div className="glass-heavy rounded-xl p-4 flex items-center justify-between flex-wrap gap-3 border-l-4 border-l-brand-primary">
                <div className="flex items-center gap-3">
                  <Tag className="w-6 h-6 text-brand-primary" />
                  <div>
                    <div className="text-white font-bold">Challenge Plan Prices & Rules</div>
                    <div className="text-xs text-zinc-400">Override default prices, profit targets, drawdowns, splits — changes apply instantly site-wide</div>
                  </div>
                </div>
                <button onClick={fetchAll} className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-xs rounded-lg flex items-center gap-1.5" data-testid="plans-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
              </div>
              <div className="glass-heavy rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/5">
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Plan</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Type</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Price ($)</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Target %</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Max DD %</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Daily Loss %</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Split %</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Min Days</th>
                      <th className="text-left p-3 text-xs uppercase text-zinc-500">Actions</th>
                    </tr></thead>
                    <tbody>{adminPlans.length === 0 ? (
                      <tr><td colSpan="9" className="p-8 text-center text-zinc-500">Loading...</td></tr>
                    ) : adminPlans.map((p, i) => {
                      const edits = planEdits[p.id] || {};
                      const hasEdits = Object.keys(edits).length > 0;
                      const InputCell = (field, defaultVal, step = 1) => (
                        <input type="number" step={step} value={edits[field] !== undefined ? edits[field] : defaultVal}
                          onChange={e => setPlanField(p.id, field, e.target.value)}
                          className="w-20 px-2 py-1 bg-[#050505] border border-white/10 rounded text-white text-xs font-mono focus:border-brand-primary outline-none"
                          data-testid={`plan-${p.id}-${field}`} />
                      );
                      return (
                        <tr key={p.id} className="border-b border-white/5" data-testid={`plan-row-${i}`}>
                          <td className="p-3"><div className="text-white text-xs font-semibold">{p.name}</div>{p.has_override && <span className="text-[9px] text-brand-primary uppercase tracking-wider font-bold">Override Active</span>}</td>
                          <td className="p-3 text-xs text-zinc-300 capitalize">{p.challenge_type}</td>
                          <td className="p-3">{InputCell('price', p.price, 0.5)}</td>
                          <td className="p-3">{InputCell('profit_target', p.profit_target, 0.5)}</td>
                          <td className="p-3">{InputCell('max_drawdown', p.max_drawdown, 0.5)}</td>
                          <td className="p-3">{InputCell('daily_loss_limit', p.daily_loss_limit, 0.5)}</td>
                          <td className="p-3">{InputCell('profit_split', p.profit_split, 1)}</td>
                          <td className="p-3">{InputCell('min_trading_days', p.min_trading_days, 1)}</td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <button onClick={() => savePlan(p.id)} disabled={!hasEdits || planBusyId === p.id} className={`px-2 py-1 text-[10px] font-bold rounded flex items-center gap-1 ${hasEdits ? 'bg-brand-positive text-black' : 'bg-white/5 text-zinc-600'} disabled:opacity-50`} data-testid={`plan-save-${i}`}><Save className="w-3 h-3" /> Save</button>
                              {p.has_override && <button onClick={() => resetPlan(p.id)} className="px-2 py-1 bg-yellow-400/10 text-yellow-400 text-[10px] font-bold rounded flex items-center gap-1" data-testid={`plan-reset-${i}`}><RotateCcw className="w-3 h-3" /> Reset</button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === 'affiliates' && (
            <div className="space-y-6">
              {/* Create Affiliate */}
              <div className="glass-heavy rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2"><Gift className="w-5 h-5 text-brand-primary" /> Assign Private Affiliate Access</h3>
                <p className="text-sm text-zinc-400 mb-4">Affiliate program is invitation-only. User must have an existing account.</p>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <input placeholder="User Email" value={affForm.email} onChange={e => setAffForm({...affForm, email: e.target.value})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="aff-email" />
                  <input placeholder="PROMO CODE" value={affForm.promo_code} onChange={e => setAffForm({...affForm, promo_code: e.target.value.toUpperCase()})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm font-mono uppercase" data-testid="aff-code" />
                  <input type="number" step="0.1" placeholder="Discount %" value={affForm.discount_pct} onChange={e => setAffForm({...affForm, discount_pct: e.target.value})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="aff-discount" />
                  <input type="number" step="0.1" placeholder="Commission %" value={affForm.commission_pct} onChange={e => setAffForm({...affForm, commission_pct: e.target.value})} className="px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="aff-commission" />
                  <button onClick={handleCreateAffiliate} className="px-4 py-2 bg-brand-primary text-black font-semibold rounded-lg text-sm hover:glow-primary transition-all" data-testid="create-affiliate-btn"><Plus className="w-4 h-4 inline mr-1" />Assign</button>
                </div>
              </div>

              {/* Affiliates Table */}
              <div className="glass-heavy rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-white/5">
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">User</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Email</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Code</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Discount</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Commission</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Sales</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Revenue</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Earned</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Status</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Actions</th>
                  </tr></thead>
                  <tbody>{affiliates.length === 0 ? <tr><td colSpan="10" className="p-8 text-center text-zinc-500">No affiliates yet. Assign one above.</td></tr> : affiliates.map((a, i) => (
                    <tr key={a.id} className="border-b border-white/5" data-testid={`affiliate-row-${i}`}>
                      <td className="p-3 text-white">{a.name}</td>
                      <td className="p-3 text-zinc-400">{a.email}</td>
                      <td className="p-3 text-brand-primary font-mono font-bold">{a.promo_code}</td>
                      <td className="p-3 text-white">{a.discount_pct}%</td>
                      <td className="p-3 text-brand-positive">{a.commission_pct}%</td>
                      <td className="p-3 text-white">{a.total_sales}</td>
                      <td className="p-3 text-white font-mono">${a.total_revenue}</td>
                      <td className="p-3 text-brand-positive font-mono font-bold">${a.total_commission}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${a.enabled ? 'bg-brand-positive/20 text-brand-positive' : 'bg-zinc-600/20 text-zinc-400'}`}>{a.enabled ? 'Active' : 'Disabled'}</span></td>
                      <td className="p-3 flex gap-1">
                        <button onClick={() => handleUpdateAffiliate(a.id, { enabled: !a.enabled })} className="px-2 py-1 bg-yellow-400/10 text-yellow-400 text-[10px] font-bold rounded hover:bg-yellow-400/20" data-testid={`toggle-affiliate-${i}`}>{a.enabled ? 'Disable' : 'Enable'}</button>
                        <button onClick={() => {
                          const newCode = window.prompt('New promo code:', a.promo_code);
                          const newDisc = window.prompt('New discount %:', a.discount_pct);
                          const newComm = window.prompt('New commission %:', a.commission_pct);
                          if (newCode || newDisc || newComm) {
                            const patch = {};
                            if (newCode && newCode !== a.promo_code) patch.promo_code = newCode.toUpperCase();
                            if (newDisc) patch.discount_pct = parseFloat(newDisc);
                            if (newComm) patch.commission_pct = parseFloat(newComm);
                            if (Object.keys(patch).length) handleUpdateAffiliate(a.id, patch);
                          }
                        }} className="px-2 py-1 bg-brand-primary/10 text-brand-primary text-[10px] font-bold rounded hover:bg-brand-primary/20" data-testid={`edit-affiliate-${i}`}>Edit</button>
                        <button onClick={() => handleDeleteAffiliate(a.id)} className="px-2 py-1 bg-red-500/10 text-red-400 text-[10px] font-bold rounded hover:bg-red-500/20" data-testid={`delete-affiliate-${i}`}>Remove</button>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Grant Account */}
          {tab === 'grant' && (
            <div className="glass-heavy rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">Grant Free Account to User</h3>
              <p className="text-sm text-zinc-400 mb-6">Manually assign a funded account to any user without payment.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input placeholder="User email" value={grantForm.user_email} onChange={e=>setGrantForm({...grantForm,user_email:e.target.value})} className="px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="grant-email" />
                <select value={grantForm.plan_id} onChange={e=>setGrantForm({...grantForm,plan_id:e.target.value})} className="px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="grant-plan">
                  <option value="1s-5k">5K 1-Step</option><option value="1s-10k">10K 1-Step</option><option value="1s-25k">25K 1-Step</option><option value="1s-50k">50K 1-Step</option><option value="1s-100k">100K 1-Step</option>
                  <option value="2s-5k">5K 2-Step</option><option value="2s-10k">10K 2-Step</option><option value="2s-25k">25K 2-Step</option><option value="2s-50k">50K 2-Step</option><option value="2s-100k">100K 2-Step</option>
                  <option value="in-5k">5K Instant</option><option value="in-10k">10K Instant</option><option value="in-25k">25K Instant</option><option value="in-50k">50K Instant</option><option value="in-100k">100K Instant</option>
                </select>
                <button onClick={handleGrantAccount} className="px-6 py-2.5 bg-brand-positive text-black font-semibold rounded-lg hover:opacity-90 transition-all text-sm" data-testid="grant-account-btn"><UserPlus className="w-4 h-4 inline mr-1" />Grant Account</button>
              </div>
            </div>
          )}

          {/* CSV Upload */}
          {tab === 'csv-upload' && <CsvUploadPanel onSuccess={fetchAll} />}

          {/* Certificates Management */}
          {tab === 'certificates' && (
            <div className="space-y-6">
              {/* Upload Form */}
              <div className="glass-heavy rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-brand-primary" /> Upload Certificate</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-zinc-500 uppercase mb-1">User Email</label>
                    <input type="email" value={certForm.user_email} onChange={e => setCertForm({...certForm, user_email: e.target.value})} placeholder="user@email.com" className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="cert-user-email" />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 uppercase mb-1">Certificate Title</label>
                    <input type="text" value={certForm.title} onChange={e => setCertForm({...certForm, title: e.target.value})} placeholder="e.g. Phase 1 Passed, Funded Trader" className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="cert-title" />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 uppercase mb-1">File (PDF/Image)</label>
                    <input ref={certFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={e => setCertForm({...certForm, file: e.target.files[0]})} className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm file:mr-3 file:bg-brand-primary/20 file:text-brand-primary file:border-0 file:rounded file:px-3 file:py-1 file:text-xs file:font-semibold" data-testid="cert-file" />
                  </div>
                </div>
                <button onClick={handleCertUpload} className="px-6 py-2.5 bg-brand-primary text-black font-bold rounded-lg text-sm hover:glow-primary transition-all" data-testid="cert-upload-btn">Upload Certificate</button>
              </div>

              {/* Certificates List */}
              <div className="glass-heavy rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead><tr className="border-b border-white/10 bg-white/[0.02]">
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">User</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Title</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">File</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Date</th>
                    <th className="text-left p-3 text-xs uppercase text-zinc-500">Action</th>
                  </tr></thead>
                  <tbody>
                    {certificates.length === 0 ? (
                      <tr><td colSpan="5" className="p-8 text-center text-zinc-500">No certificates uploaded yet</td></tr>
                    ) : certificates.map((c, i) => (
                      <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="p-3 text-sm text-white">{c.user_email}</td>
                        <td className="p-3 text-sm text-brand-primary font-semibold">{c.title}</td>
                        <td className="p-3 text-sm text-zinc-400">{c.filename}</td>
                        <td className="p-3 text-sm text-zinc-500">{new Date(c.created_at).toLocaleDateString()}</td>
                        <td className="p-3"><button onClick={() => handleDeleteCert(c.id)} className="px-2 py-1 bg-red-500/10 text-red-400 text-xs font-bold rounded hover:bg-red-500/20" data-testid={`delete-cert-${i}`}><Trash2 className="w-3 h-3" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* AYRONN PAY Screenshot Viewer */}
      {mpScreenshotViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" onClick={closeMpScreenshot} data-testid="mp-screenshot-modal">
          <div className="relative max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <button onClick={closeMpScreenshot} className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-white text-black rounded-full flex items-center justify-center font-bold shadow-2xl"><X className="w-5 h-5" /></button>
            <div className="bg-[#0A0F1C] border border-white/10 rounded-2xl overflow-hidden flex flex-col">
              <div className="p-4 border-b border-white/10 bg-black/40">
                <div className="text-white font-bold">{mpScreenshotViewer.full_name} — {mpScreenshotViewer.plan_name}</div>
                <div className="text-xs text-zinc-400 font-mono">UTR: {mpScreenshotViewer.utr} · ${mpScreenshotViewer.amount_usd} / ₹{(mpScreenshotViewer.amount_inr || 0).toLocaleString('en-IN')}</div>
              </div>
              <div className="flex-1 overflow-auto bg-black flex items-center justify-center p-4">
                <img src={mpScreenshotViewer.blobUrl} alt="Payment Proof" className="max-w-full max-h-[60vh] object-contain" data-testid="mp-screenshot-img" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AYRONN PAY Approve/Reject Confirmation */}
      {mpActionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="mp-action-modal">
          <div className="relative w-full max-w-md bg-[#0A0F1C] border border-white/10 rounded-2xl p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-2">
              {mpActionId.action === 'approve' ? 'Approve Payment?' : 'Reject Payment?'}
            </h3>
            <p className="text-sm text-zinc-400 mb-4">
              {mpActionId.action === 'approve'
                ? 'This will auto-allocate a trading account from inventory and notify the user via email.'
                : 'This will mark the payment as rejected and email the user with the reason below.'}
            </p>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1 font-bold">
              {mpActionId.action === 'approve' ? 'Admin note (optional)' : 'Reason for rejection *'}
            </label>
            <textarea
              value={mpActionNote} onChange={e => setMpActionNote(e.target.value)}
              placeholder={mpActionId.action === 'approve' ? 'e.g. Verified via SBI dashboard' : 'e.g. UTR not found in our records'}
              rows={3}
              className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary outline-none resize-none"
              data-testid="mp-action-note"
            />
            <div className="flex gap-2 mt-5">
              <button onClick={cancelMpAction} className="flex-1 py-2.5 bg-white/5 text-white text-sm font-bold rounded-lg hover:bg-white/10" data-testid="mp-action-cancel">Cancel</button>
              <button
                onClick={confirmMpAction}
                disabled={mpBusyId === mpActionId.id}
                className={`flex-1 py-2.5 text-sm font-bold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 ${
                  mpActionId.action === 'approve' ? 'bg-brand-positive text-black hover:opacity-90' : 'bg-brand-negative text-white hover:opacity-90'
                }`}
                data-testid="mp-action-confirm"
              >
                {mpBusyId === mpActionId.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (mpActionId.action === 'approve' ? <ThumbsUp className="w-4 h-4" /> : <ThumbsDown className="w-4 h-4" />)}
                Confirm {mpActionId.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KYC Image Viewer */}
      {kycViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" onClick={closeKycViewer} data-testid="kyc-image-modal">
          <div className="relative max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <button onClick={closeKycViewer} className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-white text-black rounded-full flex items-center justify-center font-bold shadow-2xl"><X className="w-5 h-5" /></button>
            <div className="bg-[#0A0F1C] border border-white/10 rounded-2xl overflow-hidden flex flex-col">
              <div className="p-4 border-b border-white/10 bg-black/40">
                <div className="text-white font-bold">{kycViewer.kyc.full_name} — {kycViewer.which.toUpperCase()}</div>
                <div className="text-xs text-zinc-400 font-mono">{kycViewer.which === 'aadhar' ? kycViewer.kyc.aadhar_number : kycViewer.kyc.pan_number}</div>
              </div>
              <div className="flex-1 overflow-auto bg-black flex items-center justify-center p-4">
                <img src={kycViewer.blobUrl} alt={kycViewer.which} className="max-w-full max-h-[60vh] object-contain" data-testid="kyc-img" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KYC Approve/Reject Confirmation */}
      {kycActionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="kyc-action-modal">
          <div className="relative w-full max-w-md bg-[#0A0F1C] border border-white/10 rounded-2xl p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-2">{kycActionId.action === 'approve' ? 'Approve KYC?' : 'Reject KYC?'}</h3>
            <p className="text-sm text-zinc-400 mb-4">
              {kycActionId.action === 'approve' ? 'User will be marked as KYC-verified and will receive an approval email.' : 'User will be notified with the reason below and asked to re-submit.'}
            </p>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1 font-bold">{kycActionId.action === 'approve' ? 'Admin note (optional)' : 'Reason for rejection *'}</label>
            <textarea value={kycActionNote} onChange={e => setKycActionNote(e.target.value)}
              placeholder={kycActionId.action === 'approve' ? 'e.g. Verified against UIDAI portal' : 'e.g. Aadhaar image is too blurry to read'}
              rows={3} className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary outline-none resize-none" data-testid="kyc-action-note" />
            <div className="flex gap-2 mt-5">
              <button onClick={() => setKycActionId(null)} className="flex-1 py-2.5 bg-white/5 text-white text-sm font-bold rounded-lg hover:bg-white/10" data-testid="kyc-action-cancel">Cancel</button>
              <button onClick={confirmKycAction} disabled={kycBusyId === kycActionId.id}
                className={`flex-1 py-2.5 text-sm font-bold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 ${kycActionId.action === 'approve' ? 'bg-brand-positive text-black' : 'bg-brand-negative text-white'}`}
                data-testid="kyc-action-confirm">
                {kycBusyId === kycActionId.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (kycActionId.action === 'approve' ? <ThumbsUp className="w-4 h-4" /> : <ThumbsDown className="w-4 h-4" />)}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CsvUploadPanel = ({ onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f) => {
    if (!f || !f.name.endsWith('.csv')) { toast.error('Only CSV files accepted'); return; }
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split('\n').filter(l => l.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1, 11).map(l => {
        const vals = l.split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, i) => { row[h] = vals[i] || ''; });
        return row;
      });
      setPreview({ headers, rows });
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/admin/csv-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      if (data.total_updated > 0) { toast.success(`${data.total_updated} accounts updated!`); onSuccess(); }
      if (data.total_failed > 0) toast.error(`${data.total_failed} entries failed`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="glass-heavy rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-2">CSV Data Upload</h3>
        <p className="text-sm text-zinc-400 mb-4">Upload a CSV file to bulk update trading account metrics. Required columns: Account ID, Balance, Equity, Daily Profit/Loss, Highest Equity Today, Lowest Equity Today</p>

        {/* Drop zone */}
        <div className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${dragOver ? 'border-brand-primary bg-brand-primary/5' : 'border-white/10 hover:border-white/20'}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileInputRef.current?.click()} data-testid="csv-drop-zone">
          <Upload className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
          <p className="text-white font-semibold mb-1">{file ? file.name : 'Drag & drop CSV file here'}</p>
          <p className="text-sm text-zinc-500">{file ? `${(file.size / 1024).toFixed(1)} KB` : 'or click to browse'}</p>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={e => handleFile(e.target.files[0])} className="hidden" data-testid="csv-file-input" />
        </div>
      </div>

      {/* Preview */}
      {preview.rows && preview.rows.length > 0 && (
        <div className="glass-heavy rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-white font-semibold">Preview (first {preview.rows.length} rows)</h3>
            <button onClick={handleUpload} disabled={uploading} className="px-6 py-2 bg-brand-primary text-black font-bold rounded-lg hover:glow-primary transition-all disabled:opacity-50 flex items-center gap-2" data-testid="upload-csv-btn">
              {uploading ? <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Processing...</> : <><Upload className="w-4 h-4" /> Upload & Update</>}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/5">{preview.headers.map(h => <th key={h} className="text-left p-3 text-xs uppercase text-zinc-500">{h}</th>)}</tr></thead>
              <tbody>{preview.rows.map((r, i) => <tr key={i} className="border-b border-white/5">{preview.headers.map(h => <td key={h} className="p-3 text-zinc-300">{r[h]}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="glass-heavy rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Upload Summary</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center gap-3 p-4 bg-brand-positive/5 border border-brand-positive/20 rounded-xl"><CheckCircle2 className="w-6 h-6 text-brand-positive" /><div><div className="text-2xl font-black text-brand-positive">{result.total_updated}</div><div className="text-xs text-zinc-400">Updated</div></div></div>
            <div className="flex items-center gap-3 p-4 bg-brand-negative/5 border border-brand-negative/20 rounded-xl"><AlertTriangle className="w-6 h-6 text-brand-negative" /><div><div className="text-2xl font-black text-brand-negative">{result.total_failed}</div><div className="text-xs text-zinc-400">Failed</div></div></div>
          </div>
          {result.errors.length > 0 && <div className="space-y-1">{result.errors.map((e, i) => <div key={i} className="text-sm text-brand-negative">{e}</div>)}</div>}
        </div>
      )}

      {/* CSV Format Guide */}
      <div className="glass-heavy rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-3">CSV Format Guide</h3>
        <div className="bg-[#050505] rounded-lg p-4 font-mono text-xs text-zinc-400 overflow-x-auto">
          <div className="text-brand-primary">Account ID,Balance,Equity,Daily Profit/Loss,Highest Equity Today,Lowest Equity Today</div>
          <div>#118133,25500.50,25300.00,150.25,25600.00,24900.00</div>
          <div>#AYR4F2B1C,10200.00,10150.00,-50.00,10300.00,10100.00</div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
